/**
 * QuickEditContent — schema editor body, rendered inside a FloatingWindow.
 *
 * Receives {target} = {kind, verticalId, itemId} via win.payload. Looks up
 * the live copy from useNexOs().schema and drives inline field edits that
 * PUT back through useNdsEditor().saveItemPatch.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { useNexOs } from "@/lib/nexOs";
import { useNdsEditor, FIELD_SCHEMAS } from "@/lib/ndsEditor";
import { useFloatingWindows } from "@/lib/floatingWindows";

const locateItem = (schema, kind, verticalId, itemId) => {
  if (!schema) return null;
  const v = (schema.verticals || []).find((x) => x.id === verticalId);
  if (!v) return null;
  if (kind === "kpi")  return (v.kpis  || []).find((k) => k.id === itemId) || null;
  if (kind === "step") return (v.steps || []).find((s) => s.id === itemId) || null;
  if (kind === "capability") {
    const tpl = (schema.capabilities_template || []).find((c) => c.id === itemId);
    if (tpl) return tpl;
    return Array.isArray(v.capabilities) ? (v.capabilities.find((c) => c.id === itemId) || null) : null;
  }
  return null;
};

const inputCls = "bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none px-3 py-2 text-nex-silver font-mono text-sm transition-all duration-200";

export default function QuickEditContent({ win }) {
  const { schema } = useNexOs();
  const { saveItemPatch, saving } = useNdsEditor();
  const { closeWindow } = useFloatingWindows();
  const target = win.payload;
  const item = useMemo(() => locateItem(schema, target?.kind, target?.verticalId, target?.itemId), [schema, target]);
  const fields = FIELD_SCHEMAS[target?.kind] || [];
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!item) return;
    const seed = {};
    for (const f of fields) seed[f.key] = item[f.key] ?? "";
    setForm(seed);
  }, [target?.kind, target?.itemId, item?.id]);

  if (!target) return null;
  if (!item) {
    return <div className="p-5 font-mono text-xs text-nex-muted">Item not found in current schema.</div>;
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    const patch = {};
    for (const f of fields) {
      let v = form[f.key];
      if (f.type === "number") v = v === "" ? null : Number(v);
      patch[f.key] = v;
    }
    const ok = await saveItemPatch({
      kind: target.kind,
      verticalId: target.verticalId,
      itemId: target.itemId,
      patch,
    });
    if (ok) {
      toast.success("Saved");
      closeWindow(win.id);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="p-4 space-y-3">
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1" data-testid={`nds-field-${f.key}`}>
            <span className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-muted uppercase">{f.label}</span>
            {f.type === "textarea" ? (
              <textarea rows={3} className={inputCls} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} />
            ) : f.type === "select" ? (
              <select className={inputCls} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)}>
                <option value="">—</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={f.type === "number" ? "number" : "text"}
                className={inputCls}
                value={form[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            )}
          </label>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 p-3 border-t border-white/10 bg-white/[0.02] sticky bottom-0">
        <button
          type="button"
          onClick={() => closeWindow(win.id)}
          data-testid="nds-quick-edit-cancel"
          className="px-3 py-2 border border-white/10 text-nex-chrome hover:text-nex-silver hover:border-white/30 font-mono text-[0.65rem] tracking-[0.22em] uppercase transition-all duration-200"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="nds-quick-edit-save"
          className="inline-flex items-center gap-2 px-4 py-2 bg-nex-gold text-nex-navy font-mono text-[0.65rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60 transition-all duration-200"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
